import { X } from 'lucide-react';
import { FONT_NAMES } from '@tes/domain';
import { Labeled, NumberInput, Select, Toggle } from '@tes/ui';
import type { Align, DateMode, SignerRole, VariableType } from '../lib/types';
import type { EditorField } from './editorStore';

/**
 * Prop-driven sections of the field properties panel, shared by the template
 * editor (bound to its store) and Quick send (bound to its own) so a signer
 * field is configured the same way in both.
 */

type Update = (uid: string, patch: Partial<EditorField>) => void;

/**
 * Options of a signer field. `roles` shows the role picker; Quick send omits it
 * because every field there belongs to its one recipient.
 */
export function SignerFieldOptions({
  field,
  update,
  roles,
}: {
  field: EditorField;
  update: Update;
  roles?: SignerRole[];
}) {
  return (
    <>
      {roles && (
        <Labeled label="Signer role">
          <Select
            value={field.signerRole ?? ''}
            onChange={(e) => update(field.uid, { signerRole: e.target.value })}
          >
            <option value="">— select —</option>
            {roles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label} ({r.key})
              </option>
            ))}
          </Select>
        </Labeled>
      )}
      <Toggle
        label="Required"
        checked={!!field.required}
        onChange={(v) => update(field.uid, { required: v })}
      />
      <Toggle
        label="Repeat on every page"
        checked={!!field.repeatPerPage}
        onChange={(v) => update(field.uid, { repeatPerPage: v })}
      />
      {field.type === 'date' && (
        <Labeled label="Date mode">
          <Select
            value={field.dateMode ?? 'auto'}
            onChange={(e) => update(field.uid, { dateMode: e.target.value as DateMode })}
          >
            <option value="auto">Auto (stamped at signing)</option>
            <option value="picker">Picker (signer chooses)</option>
          </Select>
        </Labeled>
      )}
      {field.type === 'text' && (
        <Labeled label="Max length (optional)">
          <NumberInput
            value={field.maxLen ?? null}
            min={1}
            onValueChange={(n) => update(field.uid, { maxLen: n ?? undefined })}
          />
        </Labeled>
      )}
      {field.type === 'checkbox' && (
        <Toggle
          label="Must be checked to complete"
          checked={!!field.mustCheck}
          onChange={(v) => update(field.uid, { mustCheck: v })}
        />
      )}
    </>
  );
}

/**
 * Per-field text style overrides. Blank/"Default" = inherit the template's
 * default text style — exactly how the merge and stamp engines resolve it.
 * Signature/initials stamp a captured image, so only Align applies; checkboxes
 * draw an X sized to the box, so Color + Align apply but Font/Size don't.
 */
export function TextStyleSection({
  field,
  style,
  update,
  variableType,
}: {
  field: EditorField;
  style: { font: string; size: number; color: string; align: Align };
  update: Update;
  variableType?: VariableType;
}) {
  // A checkbox VARIABLE is as much a checkbox as a signer's is — both stamp a
  // mark, so neither gets Font/Size.
  const isCheckbox = field.type === 'checkbox' || variableType === 'checkbox';
  const isTextual =
    !isCheckbox && (field.kind === 'variable' || field.type === 'text' || field.type === 'date');

  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Text style
      </div>
      <div className="grid grid-cols-2 gap-2">
        {isTextual && (
          <Labeled label="Font">
            <Select
              value={field.font ?? ''}
              onChange={(e) => update(field.uid, { font: e.target.value || undefined })}
            >
              <option value="">Default ({style.font})</option>
              {FONT_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Labeled>
        )}
        {isTextual && (
          <Labeled label="Size (pt)">
            <NumberInput
              value={field.fontSize ?? null}
              min={4}
              max={144}
              placeholder={field.kind === 'variable' ? `${style.size} (default)` : 'auto'}
              onValueChange={(n) => update(field.uid, { fontSize: n ?? undefined })}
            />
          </Labeled>
        )}
        {(isTextual || isCheckbox) && (
          <Labeled label="Color">
            <div className="flex items-center gap-1">
              <input
                type="color"
                value={field.fontColor ?? style.color}
                onChange={(e) => update(field.uid, { fontColor: e.target.value })}
                className="h-9 w-full rounded-md border border-slate-300"
              />
              {field.fontColor && (
                <button
                  type="button"
                  title="Reset to default color"
                  onClick={() => update(field.uid, { fontColor: undefined })}
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </Labeled>
        )}
        <Labeled label="Align">
          <Select
            value={field.align ?? ''}
            onChange={(e) =>
              update(field.uid, { align: (e.target.value || undefined) as Align | undefined })
            }
          >
            <option value="">Default ({style.align})</option>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </Select>
        </Labeled>
      </div>
      {(field.type === 'signature' || field.type === 'initials') && (
        <p className="mt-2 text-xs text-slate-400">
          Signatures stamp as captured images — only alignment applies.
        </p>
      )}
    </div>
  );
}

/** Page + box in PDF points. Bounds are the caller's `update` to enforce. */
export function PlacementSection({
  field,
  pageCount,
  update,
}: {
  field: EditorField;
  pageCount: number;
  update: Update;
}) {
  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Placement (pt)
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Labeled label="Page">
          <NumberInput
            value={field.page}
            min={1}
            max={pageCount}
            onValueChange={(n) => {
              // An empty field mid-edit keeps the current page rather than
              // jumping the box to page 1.
              if (n !== null) update(field.uid, { page: Math.min(pageCount, Math.max(1, n)) });
            }}
          />
        </Labeled>
        <div />
        <Labeled label="X">
          <NumberInput
            value={Math.round(field.x)}
            onValueChange={(n) => n !== null && update(field.uid, { x: n })}
          />
        </Labeled>
        <Labeled label="Y">
          <NumberInput
            value={Math.round(field.y)}
            onValueChange={(n) => n !== null && update(field.uid, { y: n })}
          />
        </Labeled>
        <Labeled label="Width">
          <NumberInput
            value={Math.round(field.w)}
            onValueChange={(n) => n !== null && update(field.uid, { w: Math.max(1, n) })}
          />
        </Labeled>
        <Labeled label="Height">
          <NumberInput
            value={Math.round(field.h)}
            onValueChange={(n) => n !== null && update(field.uid, { h: Math.max(1, n) })}
          />
        </Labeled>
      </div>
    </div>
  );
}
